import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Table,
  Collapse,
} from 'react-bootstrap'
import httpClient, { getBaseURL } from '../../../../utility/httpClient.util'
import { useLocation } from 'react-router-dom'
import useWebRtcRecorder from '../../../../hooks/useWebRtcRecorder'
import {
  createPcmCaptureNode,
  createSpeechRecognition,
  encodeWavBlob,
  isAudioWorkletSupported,
  isSpeechRecognitionSupported,
  mergeFloat32Chunks,
} from './audioCapture.util'
import {
  buildOrderText,
  extractDiagnoses,
  normalizeIcd10,
  normalizeOrderItems,
} from './audioAnalyzerData.util'

const ORDER_SOURCE_LABEL = {
  drug: 'ยา',
  medical_service: 'หัตถการ / บริการ',
  lab: 'แล็บ',
  radiology: 'เอกซเรย์ / รังสี',
  lab_diagnostic: 'กลุ่มตรวจแล็บ',
  department: 'แผนก',
  diet_type: 'โภชนาการ / อาหาร',
  diet_condition: 'โภชนาการ / อาหาร',
  meal_schedule: 'โภชนาการ / อาหาร',
  meal_type: 'โภชนาการ / อาหาร',
  taste_profile: 'โภชนาการ / อาหาร',
  restriction: 'โภชนาการ / อาหาร',
  protein_level: 'โภชนาการ / อาหาร',
  sodium_level: 'โภชนาการ / อาหาร',
  fat_level: 'โภชนาการ / อาหาร',
}

const orderSourceLabel = (source) => ORDER_SOURCE_LABEL[source] || 'อื่นๆ'

const groupOrderItems = (items) => {
  const groups = new Map()
  items.forEach((item) => {
    const source = item.source || ''
    if (!groups.has(source)) groups.set(source, [])
    groups.get(source).push(item)
  })
  return Array.from(groups, ([source, groupItems]) => ({
    source,
    label: orderSourceLabel(source),
    items: groupItems,
  }))
}

const MEDIA_GROUP_CODE = 'SPEECHTOTEXT'
const CATEGORY_ANALYZER_CODE = 'AUDIO_OPD_SCREENING'
const RECORDING_PROFILE = 'DESIGNER_AUDIO'
const PATIENT_AUDIO_MODULE = 'PATIENT_EVALUATION_AUDIO'
const AUDIO_ANALYSIS_TEMPLATE_ID = 'patient_evaluation_audio_analysis.v1'
const AUDIO_HISTORY_PAGE_SIZE = 20
const ICD10_ANALYZER_CODE = 'ICD10'
const DOCTORORDER_ANALYZER_CODE = 'DOCTORORDER'
const PRIMARY_SPEAKER = 'หมอ'
const MAX_AUDIO_BYTES = 25 * 1024 * 1024
const MAX_RECORD_SECONDS_CLIENT = 270
const MAX_RECORD_SECONDS_SERVER = 4200
const CHUNK_TARGET_MS = 480000
const CHUNK_BOUNDARY_WINDOW_MS = 60000
const ACCEPTED_EXTENSIONS = ['wav', 'mp3']
const SPEECH_RESTART_DELAY_MS = 300
const SPEECH_MAX_RESTART_FAILURES = 5
const LOCAL_CAPTURE_MAX_BYTES = 48 * 1024 * 1024
const BUFFER_SIZE = 4096
const DURATION_TICK_MS = 1000
const FLOATING_DRAWER_TOP = 56
const FLOATING_DRAWER_WIDTH = 560
const FLOATING_HANDLE_WIDTH = 74
const FLOATING_VIEWPORT_GAP = 14

const audioAnalyzerPanelStyle = {
  padding: '0 16px 16px',
  fontSize: 14,
}

const primaryAnalyzerCardBodyStyle = {
  paddingTop: 12,
}

const floatingRootStyle = {
  position: 'fixed',
  top: FLOATING_DRAWER_TOP,
  right: 0,
  bottom: 0,
  width: FLOATING_DRAWER_WIDTH,
  maxWidth: 'calc(100vw - ' + (FLOATING_HANDLE_WIDTH + FLOATING_VIEWPORT_GAP) + 'px)',
  zIndex: 1030,
  pointerEvents: 'none',
}

const getFloatingPanelStyle = (isOpen) => ({
  position: 'relative',
  width: '100%',
  height: 'calc(100vh - ' + FLOATING_DRAWER_TOP + 'px)',
  backgroundColor: '#fff',
  borderLeft: '1px solid #e9ecef',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.22)',
  transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 220ms ease',
  pointerEvents: 'auto',
})

const getFloatingHandleStyle = (isOpen, isDragging, isRecordingClosed) => ({
  position: 'absolute',
  top: 120,
  left: -FLOATING_HANDLE_WIDTH,
  width: FLOATING_HANDLE_WIDTH,
  height: 88,
  border: 0,
  borderRadius: '44px 0 0 44px',
  background: 'linear-gradient(135deg, #4099ff 0%, #6554f3 56%, #7c3aed 100%)',
  color: '#fff',
  boxShadow: isRecordingClosed
    ? '0 10px 24px rgba(64, 153, 255, 0.35), 0 0 0 0 rgba(255, 77, 79, 0.35)'
    : '0 10px 24px rgba(64, 153, 255, 0.35), 0 0 18px rgba(124, 58, 237, 0.18)',
  cursor: isDragging ? 'grabbing' : 'grab',
  outline: 'none',
  padding: 0,
  animation: isRecordingClosed ? 'audio-analyzer-handle-recording 1.35s ease-in-out infinite' : 'none',
})

const floatingHandleContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  lineHeight: 1,
}

const floatingHandleAiStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  marginBottom: 7,
}

const floatingHandleMicStyle = {
  fontSize: 15,
}

const floatingHandleChevronStyle = {
  fontSize: 24,
}

const floatingRecordingDotStyle = {
  position: 'absolute',
  top: 12,
  right: 10,
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: '#ff4d4f',
  boxShadow: '0 0 0 0 rgba(255, 77, 79, 0.62)',
  animation: 'audio-analyzer-recording-dot 1.15s ease-out infinite',
}

const floatingRecordingTextStyle = {
  position: 'absolute',
  left: 13,
  bottom: 10,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0,
}

const floatingRecordingAnimationStyle = `
@keyframes audio-analyzer-handle-recording {
  0% { box-shadow: 0 10px 24px rgba(64, 153, 255, 0.35), 0 0 0 0 rgba(255, 77, 79, 0.32); }
  70% { box-shadow: 0 10px 24px rgba(64, 153, 255, 0.35), 0 0 0 14px rgba(255, 77, 79, 0); }
  100% { box-shadow: 0 10px 24px rgba(64, 153, 255, 0.35), 0 0 0 0 rgba(255, 77, 79, 0); }
}
@keyframes audio-analyzer-recording-dot {
  0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.62); }
  75% { box-shadow: 0 0 0 8px rgba(255, 77, 79, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
}
.audio-analyzer-turn-bubble {
  max-width: 85%;
}
.audio-analyzer-doctor-bubble {
  background-color: #e6edf5 !important;
  border: 1px solid #cbd5e1 !important;
  color: #1f2937 !important;
}
.audio-analyzer-doctor-bubble * {
  color: #1f2937 !important;
}
.audio-analyzer-patient-bubble {
  background-color: #4099ff !important;
  border: 1px solid #4099ff !important;
  color: #ffffff !important;
}
.audio-analyzer-patient-bubble * {
  color: #ffffff !important;
}

`

const getFloatingBodyStyle = (isOpen) => ({
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: isOpen ? 'block' : 'none',
})

const recordActionRowStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
}

const panelActionButtonStyle = {
  flex: '0 0 auto',
  minWidth: 92,
  whiteSpace: 'nowrap',
}

const recordedAudioStyle = {
  flex: '1 1 260px',
  minWidth: 220,
  maxWidth: '100%',
}

const historyAudioStyle = {
  width: '100%',
  maxWidth: '100%',
}

const historyItemStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  padding: 10,
  marginBottom: 10,
}

const tabTitleIconStyle = {
  fontSize: 14,
}

const tabListStyle = {
  display: 'flex',
  alignItems: 'flex-end',
  borderBottom: '1px solid #d9e2ec',
  marginBottom: 16,
  minWidth: 0,
  overflowX: 'auto',
}

const getTabButtonStyle = (isActive, isDisabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minWidth: 112,
  minHeight: 40,
  padding: '8px 14px',
  border: '1px solid #d9e2ec',
  borderBottomColor: isActive ? '#fff' : '#d9e2ec',
  borderRadius: '6px 6px 0 0',
  backgroundColor: isActive ? '#fff' : '#f8fafc',
  color: isActive ? '#4b5563' : '#6b7280',
  fontSize: 14,
  fontWeight: isActive ? 500 : 400,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  opacity: isDisabled ? 0.6 : 1,
  marginRight: 6,
  marginBottom: isActive ? -1 : 0,
})

const MODE = { record: 'record', upload: 'upload' }
const STATUS = {
  idle: 'idle',
  uploading: 'uploading',
  analyzing: 'analyzing',
  done: 'done',
  error: 'error',
}

const validateUploadFile = (selected) => {
  if (!selected) return 'กรุณาเลือกไฟล์เสียงก่อน'
  const extension = (selected.name.split('.').pop() || '').toLowerCase()
  if (!ACCEPTED_EXTENSIONS.includes(extension)) {
    return 'รองรับเฉพาะไฟล์ wav หรือ mp3'
  }
  if (selected.size > MAX_AUDIO_BYTES) return 'ไฟล์ใหญ่เกิน 25 MB'
  return ''
}

const renderValue = (value) => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const formatDuration = (totalSeconds) => {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return minutes + ':' + seconds
}

const buildQueryString = (params = {}) => {
  const queryParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      queryParams.set(key, value)
    }
  })
  const queryString = queryParams.toString()
  return queryString ? '?' + queryString : ''
}

const getResponseData = (response) =>
  response && response.data ? response.data.data || response.data : null

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.rows)) return payload.rows
  return []
}

const getMediaAudioUrl = (attachmentId) =>
  attachmentId ? getBaseURL() + '/media/attachment/originalFile/' + attachmentId : ''

const normalizeStoredAnalysisResult = (recording = {}) => {
  if (recording.analysisResult && typeof recording.analysisResult === 'object') {
    return recording.analysisResult
  }
  if (recording.formData || recording.turns || recording.unmappedFields) {
    return {
      formData: recording.formData || {},
      turns: Array.isArray(recording.turns) ? recording.turns : [],
      unmappedFields: Array.isArray(recording.unmappedFields)
        ? recording.unmappedFields
        : [],
    }
  }
  return null
}

const getAudioFileName = (fileName, attachmentId) =>
  fileName || (attachmentId ? 'audio-' + attachmentId + '.mp3' : 'patient-audio')

const AudioAnalyzerPanel = ({ onRecordingChange }) => {
  const [mode, setMode] = useState(MODE.record)
  const [status, setStatus] = useState(STATUS.idle)
  const [errorMessage, setErrorMessage] = useState('')
  const [result, setResult] = useState(null)
  const [showRawJson, setShowRawJson] = useState(false)
  const [icd10Items, setIcd10Items] = useState([])
  const [doctorOrder, setDoctorOrder] = useState({ status: 'idle', items: [] })

  const [file, setFile] = useState(null)

  const [isRecording, setIsRecording] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [durationSec, setDurationSec] = useState(0)
  const [recordedBlob, setRecordedBlob] = useState(null)
  const [recordedUrl, setRecordedUrl] = useState('')
  const [sentences, setSentences] = useState([])
  const [interimText, setInterimText] = useState('')
  const [speechWarning, setSpeechWarning] = useState('')
  const [speechSupported] = useState(() => isSpeechRecognitionSupported())
  const [useServerRecording, setUseServerRecording] = useState(false)
  const [serverAttachmentId, setServerAttachmentId] = useState(null)
  const [mediaAttachmentId, setMediaAttachmentId] = useState('')
  const [patientFileId, setPatientFileId] = useState(null)
  const [audioHistory, setAudioHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [analysisRecord, setAnalysisRecord] = useState(null)

  const location = useLocation()
  const patientIdentifier = useMemo(() => {
    const queryParams = new URLSearchParams(location.search || '')
    return {
      hn: queryParams.get('hn') || '',
      vn: queryParams.get('vn') || '',
      an: queryParams.get('an') || '',
    }
  }, [location.search])
  const { hn, vn, an } = patientIdentifier

  const serverRecorder = useWebRtcRecorder({ profile: RECORDING_PROFILE })
  const serverAttachmentIdRef = useRef(null)
  const cutPointsRef = useRef([])
  const nextChunkTargetMsRef = useRef(CHUNK_TARGET_MS)
  const recordingStartMsRef = useRef(0)
  const maxRecordSeconds = useServerRecording
    ? MAX_RECORD_SECONDS_SERVER
    : MAX_RECORD_SECONDS_CLIENT

  const streamRef = useRef(null)
  const audioContextRef = useRef(null)
  const sourceRef = useRef(null)
  const processorRef = useRef(null)
  const chunksRef = useRef([])
  const localByteCountRef = useRef(0)
  const recognitionRef = useRef(null)
  const isRecordingRef = useRef(false)
  const startingRef = useRef(false)
  const stoppingRef = useRef(false)
  const analysisRunRef = useRef(0)
  const speechGenRef = useRef(0)
  const durationTimerRef = useRef(null)
  const committedFinalCountRef = useRef(0)
  const lastInterimRef = useRef('')
  const speechFatalRef = useRef(false)
  const restartTimerRef = useRef(null)
  const restartFailRef = useRef(0)
  const sessionGotResultRef = useRef(false)
  const recordedUrlRef = useRef('')
  const mediaAttachmentIdRef = useRef('')
  const patientFileRef = useRef(null)
  const mountedRef = useRef(true)

  const isBusy = status === STATUS.uploading || status === STATUS.analyzing

  const loadAnalysisComposition = useCallback(async () => {
    if (!vn) return null
    const queryString = buildQueryString({ vn, isPluralResult: true })
    const response = await httpClient
      .get('/ehr/composition/' + AUDIO_ANALYSIS_TEMPLATE_ID + queryString)
      .catch(() => null)
    const payload = getResponseData(response)
    const record = Array.isArray(payload) ? payload[0] || null : payload || null
    return record && record.vn ? record : null
  }, [vn])

  const loadAudioHistory = useCallback(async () => {
    if (!hn && !vn) {
      setAudioHistory([])
      setAnalysisRecord(null)
      return
    }
    setHistoryLoading(true)
    try {
      const queryString = buildQueryString({
        hn,
        vn,
        an,
        module: PATIENT_AUDIO_MODULE,
        page: 1,
        perPage: AUDIO_HISTORY_PAGE_SIZE,
      })
      const [patientFileResponse, composition] = await Promise.all([
        httpClient
          .get('/document/patient-file/searchlist' + queryString)
          .catch(() => null),
        loadAnalysisComposition(),
      ])
      const patientFiles = normalizeList(getResponseData(patientFileResponse))
      const recordings = Array.isArray(composition?.recordings)
        ? composition.recordings
        : []
      const recordingByAttachmentId = new Map(
        recordings
          .filter((item) => item?.mediaAttachmentId)
          .map((item) => [item.mediaAttachmentId, item]),
      )
      const fileAttachmentIds = new Set()
      const fileItems = patientFiles.map((item) => {
        const recording = recordingByAttachmentId.get(item.mediaAttachmentId) || {}
        if (item.mediaAttachmentId) fileAttachmentIds.add(item.mediaAttachmentId)
        return {
          ...item,
          recording,
          analysisResult: normalizeStoredAnalysisResult(recording),
          audioUrl: getMediaAudioUrl(item.mediaAttachmentId),
        }
      })
      const recordingOnlyItems = recordings
        .filter((item) => item?.mediaAttachmentId && !fileAttachmentIds.has(item.mediaAttachmentId))
        .map((item) => ({
          patientFileId: item.patientFileId || null,
          mediaAttachmentId: item.mediaAttachmentId,
          name: getAudioFileName(item.fileName, item.mediaAttachmentId),
          recording: item,
          analysisResult: normalizeStoredAnalysisResult(item),
          audioUrl: getMediaAudioUrl(item.mediaAttachmentId),
        }))
      setAnalysisRecord(composition)
      setAudioHistory([...fileItems, ...recordingOnlyItems])
    } finally {
      if (mountedRef.current) setHistoryLoading(false)
    }
  }, [an, hn, loadAnalysisComposition, vn])

  useEffect(() => {
    loadAudioHistory()
  }, [loadAudioHistory])

  useEffect(() => {
    mediaAttachmentIdRef.current = mediaAttachmentId
  }, [mediaAttachmentId])

  useEffect(() => {
    patientFileRef.current = patientFileId ? { patientFileId } : null
  }, [patientFileId])

  useEffect(() => {
    if (onRecordingChange) {
      onRecordingChange({
        durationSec,
        isRecording,
      })
    }
  }, [durationSec, isRecording, onRecordingChange])

  const updateRecordedUrl = (url) => {
    if (recordedUrlRef.current) URL.revokeObjectURL(recordedUrlRef.current)
    recordedUrlRef.current = url
    setRecordedUrl(url)
  }

  const recordChunkBoundaryIfDue = () => {
    if (!useServerRecording || !isRecordingRef.current) return
    if (!recordingStartMsRef.current) return
    const elapsedMs = Date.now() - recordingStartMsRef.current
    if (elapsedMs >= nextChunkTargetMsRef.current - CHUNK_BOUNDARY_WINDOW_MS) {
      cutPointsRef.current.push(elapsedMs)
      nextChunkTargetMsRef.current = elapsedMs + CHUNK_TARGET_MS
    }
  }

  const pushFinalSentence = (text) => {
    if (!mountedRef.current) return
    const trimmed = text.trim()
    if (!trimmed) return
    setSentences((prev) => [...prev, trimmed])
    recordChunkBoundaryIfDue()
  }

  const flushInterim = () => {
    pushFinalSentence(lastInterimRef.current)
    lastInterimRef.current = ''
    if (mountedRef.current) setInterimText('')
  }

  const scheduleSpeechRestart = () => {
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current)
    restartTimerRef.current = setTimeout(() => {
      if (isRecordingRef.current && !speechFatalRef.current) launchRecognition()
    }, SPEECH_RESTART_DELAY_MS)
  }

  const handleSpeechRestartFailure = () => {
    restartFailRef.current += 1
    if (restartFailRef.current >= SPEECH_MAX_RESTART_FAILURES) {
      setSpeechWarning('ถอดเสียงสดหยุดทำงาน (ยังอัดเสียงเพื่อวิเคราะห์ได้ปกติ)')
      return
    }
    scheduleSpeechRestart()
  }

  const launchRecognition = () => {
    const recognition = createSpeechRecognition()
    if (!recognition) return
    const generation = speechGenRef.current
    committedFinalCountRef.current = 0
    lastInterimRef.current = ''
    sessionGotResultRef.current = false
    recognition.onresult = (event) => {
      if (generation !== speechGenRef.current) return
      sessionGotResultRef.current = true
      let interim = ''
      for (let index = 0; index < event.results.length; index += 1) {
        const speechResult = event.results[index]
        const transcript = speechResult[0].transcript
        if (speechResult.isFinal) {
          if (index >= committedFinalCountRef.current) {
            pushFinalSentence(transcript)
            committedFinalCountRef.current = index + 1
          }
        } else {
          interim += transcript
        }
      }
      lastInterimRef.current = interim
      if (mountedRef.current) setInterimText(interim)
    }
    recognition.onerror = (event) => {
      if (generation !== speechGenRef.current) return
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        speechFatalRef.current = true
        setSpeechWarning('ถอดเสียงสดถูกปฏิเสธสิทธิ์ไมโครโฟน (ยังอัดเสียงเพื่อวิเคราะห์ได้ปกติ)')
      }
    }
    recognition.onend = () => {
      if (generation !== speechGenRef.current) return
      flushInterim()
      if (!isRecordingRef.current || speechFatalRef.current) return
      if (sessionGotResultRef.current) {
        restartFailRef.current = 0
        scheduleSpeechRestart()
      } else {
        handleSpeechRestartFailure()
      }
    }
    recognitionRef.current = recognition
    try {
      recognition.start()
    } catch (startError) {
      handleSpeechRestartFailure()
    }
  }

  const startSpeech = () => {
    if (!speechSupported) return
    speechFatalRef.current = false
    restartFailRef.current = 0
    speechGenRef.current += 1
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current)
    launchRecognition()
  }

  const stopAudioGraph = () => {
    if (processorRef.current) {
      if (processorRef.current.port) {
        processorRef.current.port.onmessage = null
      } else {
        processorRef.current.onaudioprocess = null
      }
      processorRef.current.disconnect()
      processorRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }

  const startLocalCapture = async (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    const audioContext = new AudioContextClass()
    audioContextRef.current = audioContext
    if (audioContext.state === 'suspended') await audioContext.resume()
    const source = audioContext.createMediaStreamSource(stream)
    sourceRef.current = source
    const pushChunk = (chunk) => {
      if (!isRecordingRef.current) return
      const nextByteCount = localByteCountRef.current + chunk.length * 4
      if (nextByteCount > LOCAL_CAPTURE_MAX_BYTES) return
      chunksRef.current.push(chunk)
      localByteCountRef.current = nextByteCount
    }
    const buildScriptProcessorNode = () => {
      const node = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1)
      node.onaudioprocess = (event) => {
        pushChunk(new Float32Array(event.inputBuffer.getChannelData(0)))
      }
      return node
    }
    let captureNode = null
    if (isAudioWorkletSupported(audioContext)) {
      try {
        captureNode = await createPcmCaptureNode(audioContext, pushChunk)
      } catch (workletError) {
        captureNode = null
      }
    }
    if (!mountedRef.current) {
      if (captureNode) captureNode.disconnect()
      return false
    }
    if (!captureNode) captureNode = buildScriptProcessorNode()
    processorRef.current = captureNode
    const muteGain = audioContext.createGain()
    muteGain.gain.value = 0
    source.connect(captureNode)
    captureNode.connect(muteGain)
    muteGain.connect(audioContext.destination)
    return true
  }

  const startRecording = async () => {
    if (isRecordingRef.current || startingRef.current || stoppingRef.current) return
    startingRef.current = true
    setIsStarting(true)
    setSentences([])
    setInterimText('')
    setSpeechWarning('')
    setRecordedBlob(null)
    updateRecordedUrl('')
    setMediaAttachmentId('')
    setPatientFileId(null)
    patientFileRef.current = null
    mediaAttachmentIdRef.current = ''
    setResult(null)
    setIcd10Items([])
    setDoctorOrder({ status: 'idle', items: [] })
    setErrorMessage('')
    setStatus(STATUS.idle)
    setDurationSec(0)
    analysisRunRef.current += 1
    chunksRef.current = []
    localByteCountRef.current = 0
    serverAttachmentIdRef.current = null
    setServerAttachmentId(null)
    cutPointsRef.current = []
    nextChunkTargetMsRef.current = CHUNK_TARGET_MS
    recordingStartMsRef.current = 0
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      if (useServerRecording) {
        try {
          await serverRecorder.start(stream)
        } catch (serverStartError) {
          setSpeechWarning('บันทึกผ่าน server ไม่สำเร็จ ใช้การอัดในเครื่องแทน')
        }
      }
      const localCaptureReady = await startLocalCapture(stream)
      if (!localCaptureReady) return
      isRecordingRef.current = true
      recordingStartMsRef.current = Date.now()
      setIsRecording(true)
      durationTimerRef.current = setInterval(
        () => setDurationSec((prev) => prev + 1),
        DURATION_TICK_MS,
      )
      startSpeech()
    } catch (error) {
      isRecordingRef.current = false
      setErrorMessage('ไม่สามารถเข้าถึงไมโครโฟน: ' + (error.message || error))
      setStatus(STATUS.error)
      stopAudioGraph()
    } finally {
      startingRef.current = false
      if (mountedRef.current) setIsStarting(false)
    }
  }

  const stopRecording = async () => {
    if (!isRecordingRef.current || stoppingRef.current) return
    isRecordingRef.current = false
    stoppingRef.current = true
    setIsRecording(false)
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current)
    if (durationTimerRef.current) clearInterval(durationTimerRef.current)
    flushInterim()
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop()
      } catch (stopError) {
        recognitionRef.current = null
      }
    }
    try {
      let resolvedServerAttachmentId = null
      if (useServerRecording) {
        try {
          resolvedServerAttachmentId = await serverRecorder.stop()
        } catch (serverStopError) {
          resolvedServerAttachmentId = null
        }
      }
      serverAttachmentIdRef.current = resolvedServerAttachmentId
      setServerAttachmentId(resolvedServerAttachmentId)
      const sampleRate = audioContextRef.current ? audioContextRef.current.sampleRate : 44100
      const merged = mergeFloat32Chunks(chunksRef.current)
      chunksRef.current = []
      localByteCountRef.current = 0
      stopAudioGraph()
      const closingContext = audioContextRef.current
      audioContextRef.current = null
      if (closingContext) {
        try {
          await closingContext.close()
        } catch (closeError) {
          setSpeechWarning('ปิดอุปกรณ์เสียงไม่สมบูรณ์')
        }
      }
      if (resolvedServerAttachmentId) {
        try {
          setStatus(STATUS.uploading)
          await persistPatientAudioAttachment({
            attachmentId: resolvedServerAttachmentId,
            fileName: 'server-recording-' + Date.now() + '.mp3',
            source: 'server-recording',
          })
          if (mountedRef.current) setStatus(STATUS.idle)
        } catch (persistError) {
          setErrorMessage('บันทึกไฟล์เสียงใน PatientFile ไม่สำเร็จ: ' + (persistError.message || persistError))
          setStatus(STATUS.error)
        }
        return
      }
      if (merged.length === 0) {
        setErrorMessage('ไม่พบเสียงที่อัดไว้')
        setStatus(STATUS.error)
        return
      }
      const blob = encodeWavBlob(merged, sampleRate)
      if (blob.size > MAX_AUDIO_BYTES) {
        setRecordedBlob(null)
        updateRecordedUrl('')
        setErrorMessage('ไฟล์ที่อัดใหญ่เกิน 25 MB กรุณาอัดสั้นลง')
        setStatus(STATUS.error)
        return
      }
      if (useServerRecording) {
        setSpeechWarning('server ไม่ได้รับเสียง — ใช้เสียงที่อัดในเครื่องแทน')
      }
      setRecordedBlob(blob)
      updateRecordedUrl(URL.createObjectURL(blob))
      try {
        const uploadFile = new File([blob], 'recording-' + Date.now() + '.wav', {
          type: 'audio/wav',
        })
        setStatus(STATUS.uploading)
        const attachmentId = await uploadToMedia(uploadFile)
        await persistPatientAudioAttachment({
          attachmentId,
          fileName: uploadFile.name,
          source: 'client-recording',
        })
        if (mountedRef.current) setStatus(STATUS.idle)
      } catch (uploadError) {
        setErrorMessage('บันทึกไฟล์เสียงเข้า media ไม่สำเร็จ: ' + (uploadError.message || uploadError))
        setStatus(STATUS.error)
      }
    } finally {
      stoppingRef.current = false
    }
  }

  useEffect(() => {
    if (isRecording && durationSec >= maxRecordSeconds) {
      stopRecording()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationSec, isRecording])

  useEffect(
    () => () => {
      mountedRef.current = false
      isRecordingRef.current = false
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current)
      if (durationTimerRef.current) clearInterval(durationTimerRef.current)
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch (cleanupError) {
          recognitionRef.current = null
        }
      }
      stopAudioGraph()
      if (audioContextRef.current) audioContextRef.current.close()
      if (recordedUrlRef.current) URL.revokeObjectURL(recordedUrlRef.current)
      serverRecorder.cancel()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const onSelectFile = (event) => {
    setFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)
    analysisRunRef.current += 1
    setMediaAttachmentId('')
    setPatientFileId(null)
    mediaAttachmentIdRef.current = ''
    patientFileRef.current = null
    setResult(null)
    setIcd10Items([])
    setDoctorOrder({ status: 'idle', items: [] })
    setErrorMessage('')
    setStatus(STATUS.idle)
  }

  const uploadToMedia = async (uploadFile) => {
    const formData = new FormData()
    formData.append('file', uploadFile)
    const { data: body } = await httpClient.post(
      `/media/attachment/uploads/${MEDIA_GROUP_CODE}?titleName=${encodeURIComponent(
        uploadFile.name,
      )}`,
      formData,
      { headers: { 'content-type': 'multipart/form-data' } },
    )
    const uploaded = Array.isArray(body && body.data) ? body.data[0] : body && body.data
    const attachmentId = uploaded && uploaded.attachmentId
    if (!attachmentId) throw new Error('อัปโหลดไฟล์ไม่สำเร็จ (ไม่พบ attachmentId)')
    return attachmentId
  }

  const analyzeAudio = async (attachmentId, segmentBoundariesMs) => {
    const payload = {
      categoryAnalyzerCode: CATEGORY_ANALYZER_CODE,
      attachmentId,
      format: 'audio',
    }
    if (Array.isArray(segmentBoundariesMs) && segmentBoundariesMs.length) {
      payload.segmentBoundariesMs = segmentBoundariesMs
    }
    const { data: body } = await httpClient.post('/designer/attachment-analyzer', payload)
    return body && body.data
  }

  const analyzeIcd10 = async (diagnosisText) => {
    const { data: body } = await httpClient.post('/designer/text-analyzer', {
      categoryAnalyzerCode: ICD10_ANALYZER_CODE,
      prompt: diagnosisText,
      format: 'text',
    })
    return normalizeIcd10(body)
  }

  const isStaleRun = (runId) => !mountedRef.current || analysisRunRef.current !== runId

  const runIcd10Lookup = async (analyzed, runId) => {
    const diagnoses = extractDiagnoses(analyzed)
    if (isStaleRun(runId)) return
    setIcd10Items([])
    for (const diagnosis of diagnoses) {
      if (isStaleRun(runId)) return
      setIcd10Items((prev) => [
        ...prev,
        { diagnosis, status: 'loading', codes: [] },
      ])
      let codes = []
      let status = 'done'
      try {
        codes = await analyzeIcd10(diagnosis)
      } catch (icd10Error) {
        status = 'error'
      }
      if (isStaleRun(runId)) return
      setIcd10Items((prev) => {
        const next = [...prev]
        const lastIndex = next.length - 1
        if (lastIndex >= 0) next[lastIndex] = { ...next[lastIndex], status, codes }
        return next
      })
    }
  }

  const runDoctorOrder = async (analyzed, runId) => {
    const orderText = buildOrderText(analyzed)
    if (isStaleRun(runId)) return
    if (!orderText) {
      setDoctorOrder({ status: 'idle', items: [] })
      return
    }
    setDoctorOrder({ status: 'loading', items: [] })
    try {
      const { data: body } = await httpClient.post('/designer/text-analyzer', {
        categoryAnalyzerCode: DOCTORORDER_ANALYZER_CODE,
        prompt: orderText,
        format: 'text',
      })
      const items = normalizeOrderItems(body)
      if (!isStaleRun(runId)) setDoctorOrder({ status: 'done', items })
    } catch (orderError) {
      if (!isStaleRun(runId)) setDoctorOrder({ status: 'error', items: [] })
    }
  }

  const findPatientAudioFile = async (attachmentId) => {
    if (!attachmentId) return null
    const queryString = buildQueryString({
      mediaAttachmentId: attachmentId,
      module: PATIENT_AUDIO_MODULE,
      page: 1,
      perPage: 1,
    })
    const response = await httpClient
      .get('/document/patient-file/searchlist' + queryString)
      .catch(() => null)
    return normalizeList(getResponseData(response))[0] || null
  }

  const persistPatientAudioAttachment = async ({ attachmentId, fileName, source }) => {
    if (!attachmentId) return null
    setMediaAttachmentId(attachmentId)
    mediaAttachmentIdRef.current = attachmentId
    const existing = await findPatientAudioFile(attachmentId)
    if (existing) {
      setPatientFileId(existing.patientFileId || null)
      patientFileRef.current = existing
      await loadAudioHistory()
      return existing
    }
    if (!hn && !vn) return null
    const payload = {
      hn,
      vn,
      an,
      mediaAttachmentId: attachmentId,
      module: PATIENT_AUDIO_MODULE,
      name: getAudioFileName(fileName, attachmentId),
      note: 'Audio analyzer: ' + CATEGORY_ANALYZER_CODE + (source ? ' (' + source + ')' : ''),
    }
    const { data: body } = await httpClient.post('/document/patient-file', payload)
    const patientFile = body && body.data ? body.data : null
    setPatientFileId(patientFile?.patientFileId || null)
    patientFileRef.current = patientFile
    await loadAudioHistory()
    return patientFile
  }

  const saveAnalysisResult = async ({ attachmentId, patientFile, analyzed, fileName, source }) => {
    if (!attachmentId || !analyzed || !hn || !vn) return null
    const latestComposition = (await loadAnalysisComposition()) || analysisRecord || {}
    const recordings = Array.isArray(latestComposition.recordings)
      ? [...latestComposition.recordings]
      : []
    const nextRecording = {
      recordingKey: attachmentId,
      mediaAttachmentId: attachmentId,
      patientFileId: patientFile?.patientFileId || patientFileId || null,
      fileName: getAudioFileName(fileName || patientFile?.name, attachmentId),
      source: source || mode,
      module: PATIENT_AUDIO_MODULE,
      categoryAnalyzerCode: CATEGORY_ANALYZER_CODE,
      recordedAt: new Date().toISOString(),
      analyzedAt: new Date().toISOString(),
      analysisResult: analyzed,
      formData: analyzed.formData || {},
      turns: Array.isArray(analyzed.turns) ? analyzed.turns : [],
      unmappedFields: Array.isArray(analyzed.unmappedFields)
        ? analyzed.unmappedFields
        : [],
    }
    const existingIndex = recordings.findIndex(
      (item) => item?.mediaAttachmentId === attachmentId,
    )
    if (existingIndex >= 0) recordings[existingIndex] = nextRecording
    else recordings.unshift(nextRecording)
    const payload = {
      ...latestComposition,
      templateId: AUDIO_ANALYSIS_TEMPLATE_ID,
      hn,
      vn,
      an,
      module: PATIENT_AUDIO_MODULE,
      recordings,
    }
    const { data: body } = await httpClient.post(
      '/ehr/composition/' + AUDIO_ANALYSIS_TEMPLATE_ID + buildQueryString({ vn }),
      payload,
    )
    const saved = body && body.data ? body.data : null
    setAnalysisRecord(saved)
    await loadAudioHistory()
    return saved
  }

  const selectHistoryItem = (item) => {
    if (!item?.mediaAttachmentId) return
    analysisRunRef.current += 1
    const runId = analysisRunRef.current
    setMode(MODE.record)
    setMediaAttachmentId(item.mediaAttachmentId)
    mediaAttachmentIdRef.current = item.mediaAttachmentId
    setPatientFileId(item.patientFileId || null)
    patientFileRef.current = item
    setFile(null)
    setRecordedBlob(null)
    updateRecordedUrl('')
    setErrorMessage('')
    setIcd10Items([])
    setDoctorOrder({ status: 'idle', items: [] })
    if (item.analysisResult) {
      setResult(item.analysisResult)
      setStatus(STATUS.done)
      runIcd10Lookup(item.analysisResult, runId)
      runDoctorOrder(item.analysisResult, runId)
    } else {
      setResult(null)
      setStatus(STATUS.idle)
    }
  }

  const onAnalyze = async () => {
    let uploadFile = null
    let attachmentId = mediaAttachmentIdRef.current
    let patientFileForAnalysis = patientFileRef.current
    const serverAttachmentId = mode === MODE.record ? serverAttachmentIdRef.current : null
    if (serverAttachmentId) attachmentId = serverAttachmentId

    if (mode === MODE.record) {
      if (!attachmentId) {
        if (!recordedBlob) {
          setErrorMessage('ยังไม่มีไฟล์เสียงที่อัด')
          setStatus(STATUS.error)
          return
        }
        uploadFile = new File([recordedBlob], 'recording-' + Date.now() + '.wav', {
          type: 'audio/wav',
        })
      }
    } else if (!attachmentId) {
      const validationError = validateUploadFile(file)
      if (validationError) {
        setErrorMessage(validationError)
        setStatus(STATUS.error)
        return
      }
      uploadFile = file
    }

    setErrorMessage('')
    analysisRunRef.current += 1
    const runId = analysisRunRef.current
    setResult(null)
    setIcd10Items([])
    setDoctorOrder({ status: 'idle', items: [] })
    try {
      if (!attachmentId) {
        setStatus(STATUS.uploading)
        attachmentId = await uploadToMedia(uploadFile)
        if (isStaleRun(runId)) return
      }
      if (!patientFileForAnalysis) {
        const recordingSource = serverAttachmentId ? 'server-recording' : 'client-recording'
        patientFileForAnalysis = await persistPatientAudioAttachment({
          attachmentId,
          fileName: uploadFile?.name || file?.name || getAudioFileName(null, attachmentId),
          source: mode === MODE.record ? recordingSource : 'upload',
        })
        if (isStaleRun(runId)) return
      }
      setStatus(STATUS.analyzing)
      const analyzed = await analyzeAudio(
        attachmentId,
        serverAttachmentId ? cutPointsRef.current : undefined,
      )
      if (isStaleRun(runId)) return
      await saveAnalysisResult({
        attachmentId,
        patientFile: patientFileForAnalysis,
        analyzed,
        fileName: uploadFile?.name || file?.name,
        source: mode === MODE.record ? 'recording' : 'upload',
      })
      if (isStaleRun(runId)) return
      setResult(analyzed)
      setStatus(STATUS.done)
      runIcd10Lookup(analyzed, runId)
      runDoctorOrder(analyzed, runId)
    } catch (error) {
      if (isStaleRun(runId)) return
      const detail =
        (error && error.response && error.response.data && error.response.data.message) ||
        (error && error.message) ||
        'เกิดข้อผิดพลาดในการวิเคราะห์'
      setErrorMessage(typeof detail === 'string' ? detail : JSON.stringify(detail))
      setStatus(STATUS.error)
    }
  }

  const onChangeMode = (nextMode) => {
    if (isRecording) return
    analysisRunRef.current += 1
    setMode(nextMode)
    setResult(null)
    setIcd10Items([])
    setDoctorOrder({ status: 'idle', items: [] })
    setErrorMessage('')
    setStatus(STATUS.idle)
    setSpeechWarning('')
    setSentences([])
    setInterimText('')
    setRecordedBlob(null)
    updateRecordedUrl('')
    setFile(null)
    setDurationSec(0)
    setMediaAttachmentId('')
    setPatientFileId(null)
    mediaAttachmentIdRef.current = ''
    patientFileRef.current = null
    serverAttachmentIdRef.current = null
    setServerAttachmentId(null)
    cutPointsRef.current = []
    nextChunkTargetMsRef.current = CHUNK_TARGET_MS
    recordingStartMsRef.current = 0
  }

  const formDataEntries = result && result.formData ? Object.entries(result.formData) : []
  const turns = result && Array.isArray(result.turns) ? result.turns : []
  const unmappedFields =
    result && Array.isArray(result.unmappedFields) ? result.unmappedFields : []
  const currentAudioUrl = mediaAttachmentId
    ? getMediaAudioUrl(mediaAttachmentId)
    : recordedUrl
  const analyzeDisabled =
    isBusy ||
    isRecording ||
    (mode === MODE.record
      ? !recordedBlob && !serverAttachmentId && !mediaAttachmentId
      : !file && !mediaAttachmentId)

  return (
    <div style={audioAnalyzerPanelStyle}>
      <Card>
        <Card.Body style={primaryAnalyzerCardBodyStyle}>
          <div style={tabListStyle} role="tablist" aria-label="Audio analyzer mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === MODE.record}
              disabled={isRecording}
              style={getTabButtonStyle(mode === MODE.record, isRecording)}
              onClick={() => onChangeMode(MODE.record)}
            >
              <i className="feather icon-mic" style={tabTitleIconStyle} />
              อัดเสียง
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === MODE.upload}
              disabled={isRecording}
              style={getTabButtonStyle(mode === MODE.upload, isRecording)}
              onClick={() => onChangeMode(MODE.upload)}
            >
              <i className="feather icon-folder" style={tabTitleIconStyle} />
              อัปโหลดไฟล์
            </button>
          </div>

          {mode === MODE.record && (
            <>
              {!speechSupported && (
                <Alert variant="warning">
                  เบราว์เซอร์นี้ไม่รองรับ Web Speech API — อัดเสียงได้แต่ไม่มีถอดเสียงสด
                  (แนะนำ Chrome/Edge)
                </Alert>
              )}
              <Form.Check
                type="switch"
                id="audio-analyzer-server-recording"
                className="mb-2"
                label="บันทึกผ่าน server (ทดลอง)"
                checked={useServerRecording}
                onChange={(event) => setUseServerRecording(event.target.checked)}
                disabled={isRecording || isStarting}
              />
              <div className="mb-2" style={recordActionRowStyle}>
                {!isRecording ? (
                  <Button
                    variant="danger"
                    onClick={startRecording}
                    disabled={isBusy || isStarting}
                    style={panelActionButtonStyle}
                  >
                    ● เริ่มอัด
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={stopRecording}
                    style={panelActionButtonStyle}
                  >
                    ■ หยุดอัด ({formatDuration(durationSec)})
                  </Button>
                )}
                {currentAudioUrl && !isRecording && (
                  <audio src={currentAudioUrl} controls style={recordedAudioStyle} />
                )}
              </div>
              {speechWarning && (
                <Alert variant="warning" className="py-1 px-2 small">
                  {speechWarning}
                </Alert>
              )}
              <div className="border rounded p-2 bg-light" style={{ minHeight: 70 }}>
                <div className="small text-muted mb-1">
                  ถอดเสียงสด (Web Speech — preview เท่านั้น ผลวิเคราะห์ใช้เสียงที่อัด)
                </div>
                {sentences.length === 0 && !interimText ? (
                  <span className="text-muted">— ยังไม่มีข้อความ —</span>
                ) : (
                  <>
                    {sentences.map((sentence, index) => (
                      <div key={index}>
                        {index + 1}. {sentence}
                      </div>
                    ))}
                    {interimText && (
                      <div className="text-muted font-italic">{interimText}…</div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {mode === MODE.upload && (
            <>
              <Form.Group>
                <Form.Label>ไฟล์เสียง (wav / mp3, ไม่เกิน 25 MB)</Form.Label>
                <Form.Control
                  type="file"
                  accept=".wav,.mp3"
                  onChange={onSelectFile}
                  disabled={isBusy}
                />
              </Form.Group>
            </>
          )}

          <Button
            variant="primary"
            onClick={onAnalyze}
            disabled={analyzeDisabled}
            style={panelActionButtonStyle}
          >
            {isBusy ? (
              <>
                <Spinner as="span" size="sm" animation="border" className="mr-2" />
                {status === STATUS.uploading ? 'กำลังอัปโหลด...' : 'กำลังวิเคราะห์...'}
              </>
            ) : (
              'วิเคราะห์'
            )}
          </Button>
          {status === STATUS.error && (
            <Alert variant="danger" className="mt-3 mb-0">
              {errorMessage}
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header>
          ไฟล์เสียงของ visit นี้
          {historyLoading && <Spinner as="span" size="sm" animation="border" className="ml-2" />}
        </Card.Header>
        <Card.Body>
          {audioHistory.length === 0 ? (
            <div className="text-muted small">
              {hn || vn ? 'ยังไม่มีไฟล์เสียงที่ผูกกับ visit นี้' : 'ยังไม่มี HN/VN สำหรับผูกไฟล์เสียง'}
            </div>
          ) : (
            audioHistory.map((item, index) => (
              <div key={item.mediaAttachmentId || index} style={historyItemStyle}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="text-truncate pr-2">
                    <div>{item.name || getAudioFileName(item.recording?.fileName, item.mediaAttachmentId)}</div>
                    <div className="small text-muted">
                      {item.mediaAttachmentId || '-'}
                    </div>
                  </div>
                  <Badge variant={item.analysisResult ? 'success' : 'secondary'}>
                    {item.analysisResult ? 'วิเคราะห์แล้ว' : 'ยังไม่วิเคราะห์'}
                  </Badge>
                </div>
                {item.audioUrl && <audio src={item.audioUrl} controls style={historyAudioStyle} />}
                <div className="mt-2 d-flex flex-wrap" style={{ gap: 8 }}>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => selectHistoryItem(item)}
                  >
                    เลือกไฟล์นี้
                  </Button>
                  {item.analysisResult && (
                    <Button
                      size="sm"
                      variant="outline-success"
                      onClick={() => selectHistoryItem(item)}
                    >
                      ใช้ผลเดิม
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      {status === STATUS.done && result && (
        <>
          <Row className="mt-3">
            <Col md={mode === MODE.record ? 6 : 12}>
              <Card>
                <Card.Header>ผลวิเคราะห์บทสนทนา — {turns.length} ช่วง</Card.Header>
                <Card.Body>
                  {turns.length === 0 ? (
                    <div className="text-muted">ไม่พบบทสนทนา</div>
                  ) : (
                    turns.map((turn, index) => {
                      const isDoctor = turn.speaker === PRIMARY_SPEAKER
                      return (
                        <div
                          key={index}
                          className={`d-flex mb-2 ${
                            isDoctor ? 'justify-content-start' : 'justify-content-end'
                          }`}
                        >
                          <div
                            className={
                              'p-2 rounded audio-analyzer-turn-bubble ' +
                              (isDoctor ? 'audio-analyzer-doctor-bubble' : 'audio-analyzer-patient-bubble')
                            }
                          >
                            <div className="small font-weight-bold">
                              {turn.speaker || '-'}
                            </div>
                            <div>{turn.text}</div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </Card.Body>
              </Card>
            </Col>
            {mode === MODE.record && (
              <Col md={6}>
                <Card>
                  <Card.Header>
                    ถอดเสียงสด — {sentences.length} ประโยค
                  </Card.Header>
                  <Card.Body>
                    {sentences.length === 0 ? (
                      <div className="text-muted">— ไม่มีข้อความ —</div>
                    ) : (
                      sentences.map((sentence, index) => (
                        <div key={index} className="mb-1">
                          {index + 1}. {sentence}
                        </div>
                      ))
                    )}
                    <div className="small text-muted mt-2">
                      ใช้เทียบกับผลวิเคราะห์ด้านซ้าย
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <Card className="mt-3">
            <Card.Header>
              ข้อมูลที่สกัดได้ (formData) — {formDataEntries.length} field
              {unmappedFields.length > 0 && (
                <Badge variant="warning" className="ml-2">
                  unmapped: {unmappedFields.length}
                </Badge>
              )}
            </Card.Header>
            <Card.Body>
              {formDataEntries.length === 0 ? (
                <div className="text-muted">ไม่มีข้อมูล</div>
              ) : (
                <Table size="sm" bordered responsive className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>ฟิลด์</th>
                      <th>ค่า</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formDataEntries.map(([key, value]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{renderValue(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {unmappedFields.length > 0 && (
                <div className="mt-2 small text-muted">
                  ฟิลด์ที่ไม่แม็ป: {unmappedFields.join(', ')}
                </div>
              )}
            </Card.Body>
          </Card>

          {icd10Items.length > 0 && (
            <Card className="mt-3">
              <Card.Header>
                ICD-10 ที่แนะนำ (AI) — {icd10Items.length} diagnosis
              </Card.Header>
              <Card.Body>
                <Table size="sm" bordered responsive className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Diagnosis</th>
                      <th>ICD-10</th>
                    </tr>
                  </thead>
                  <tbody>
                    {icd10Items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.diagnosis}</td>
                        <td>
                          {item.status === 'loading' ? (
                            <span className="text-muted">
                              <Spinner
                                animation="border"
                                size="sm"
                                className="mr-2"
                              />
                              กำลังค้นรหัส ICD-10...
                            </span>
                          ) : item.status === 'error' ? (
                            <span className="text-danger small">
                              ค้นรหัสไม่สำเร็จ
                            </span>
                          ) : item.codes.length === 0 ? (
                            <span className="text-muted small">
                              ไม่พบรหัสที่ตรง
                            </span>
                          ) : (
                            item.codes.map((code, codeIdx) => (
                              <Badge
                                key={codeIdx}
                                variant="info"
                                className="mr-1 mb-1"
                                style={{ fontSize: '0.85em' }}
                              >
                                {code.code} — {code.nameTh || code.nameEn}
                              </Badge>
                            ))
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {doctorOrder.status !== 'idle' && (
            <Card className="mt-3">
              <Card.Header>คำสั่งแพทย์ที่จับคู่ได้ (DOCTORORDER)</Card.Header>
              <Card.Body>
                {doctorOrder.status === 'loading' ? (
                  <span className="text-muted">
                    <Spinner animation="border" size="sm" className="mr-2" />
                    กำลังแปลงคำสั่งแพทย์...
                  </span>
                ) : doctorOrder.status === 'error' ? (
                  <span className="text-danger small">แปลงคำสั่งไม่สำเร็จ</span>
                ) : doctorOrder.items.length === 0 ? (
                  <span className="text-muted small">ไม่พบรายการที่ตรง</span>
                ) : (
                  groupOrderItems(doctorOrder.items).map((group) => (
                    <div key={group.source || 'other'} className="mb-3">
                      <div className="text-muted small font-weight-bold mb-1 mt-2">
                        {group.label}
                      </div>
                      <Table size="sm" bordered responsive className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '20%' }}>รหัส</th>
                            <th>รายการ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.code || '-'}</td>
                              <td>
                                {item.name || (
                                  <pre
                                    className="mb-0"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                  >
                                    {JSON.stringify(item.raw, null, 2)}
                                  </pre>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          )}

          <Card className="mt-3">
            <Card.Header>
              <Button
                variant="link"
                className="p-0"
                onClick={() => setShowRawJson((prev) => !prev)}
              >
                {showRawJson ? 'ซ่อน' : 'ดู'} JSON ดิบ
              </Button>
            </Card.Header>
            <Collapse in={showRawJson}>
              <Card.Body>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </Card.Body>
            </Collapse>
          </Card>
        </>
      )}
    </div>
  )
}

export const AudioAnalyzerFloatingMenu = ({ defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [isDragging, setIsDragging] = useState(false)
  const [recordingState, setRecordingState] = useState({
    durationSec: 0,
    isRecording: false,
  })
  const dragRef = useRef(null)
  const isRecordingClosed = recordingState.isRecording && !isOpen
  const recordingDurationText = formatDuration(recordingState.durationSec)

  const beginDrag = (clientX) => {
    dragRef.current = {
      clientX,
    }
    setIsDragging(true)
  }

  const endDrag = useCallback((clientX) => {
    const drag = dragRef.current
    if (!drag) return
    const diffX = clientX - drag.clientX
    dragRef.current = null
    setIsDragging(false)
    if (Math.abs(diffX) < 12) {
      setIsOpen((prev) => !prev)
      return
    }
    setIsOpen(diffX < 0)
  }, [])

  useEffect(() => {
    if (!isDragging) return undefined

    const onMouseUp = (event) => {
      endDrag(event.clientX)
    }
    const onTouchEnd = (event) => {
      const touch = event.changedTouches && event.changedTouches[0]
      const fallbackClientX = dragRef.current ? dragRef.current.clientX : 0
      endDrag(touch ? touch.clientX : fallbackClientX)
    }
    const onTouchCancel = () => {
      dragRef.current = null
      setIsDragging(false)
    }

    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('touchend', onTouchEnd)
    window.addEventListener('touchcancel', onTouchCancel)

    return () => {
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [endDrag, isDragging])

  const onHandleMouseDown = (event) => {
    event.preventDefault()
    beginDrag(event.clientX)
  }

  const onHandleTouchStart = (event) => {
    const touch = event.touches && event.touches[0]
    if (touch) beginDrag(touch.clientX)
  }

  const onHandleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen((prev) => !prev)
    }
  }

  const onRecordingChange = useCallback((nextRecordingState) => {
    setRecordingState(nextRecordingState)
  }, [])

  return (
    <div style={floatingRootStyle}>
      <style>{floatingRecordingAnimationStyle}</style>
      <div style={getFloatingPanelStyle(isOpen)}>
        <button
          type="button"
          aria-label={isOpen ? 'Close audio analyzer' : 'Open audio analyzer'}
          title={isOpen ? 'ปิด Audio Analyzer' : 'เปิด Audio Analyzer'}
          style={getFloatingHandleStyle(isOpen, isDragging, isRecordingClosed)}
          onMouseDown={onHandleMouseDown}
          onTouchStart={onHandleTouchStart}
          onKeyDown={onHandleKeyDown}
        >
          {isRecordingClosed && <span style={floatingRecordingDotStyle} />}
          <span style={floatingHandleContentStyle}>
            <span style={floatingHandleAiStyle}>
              <i className="feather icon-mic" style={floatingHandleMicStyle} />
              AI
            </span>
            <i
              className={'feather ' + (
                isOpen ? 'icon-chevrons-right' : 'icon-chevrons-left'
              )}
              style={floatingHandleChevronStyle}
            />
            {isRecordingClosed && (
              <span style={floatingRecordingTextStyle}>REC {recordingDurationText}</span>
            )}
          </span>
        </button>
        <div style={getFloatingBodyStyle(isOpen)} aria-hidden={!isOpen}>
          <AudioAnalyzerPanel onRecordingChange={onRecordingChange} />
        </div>
      </div>
    </div>
  )
}

const AudioAnalyzer = () => <AudioAnalyzerFloatingMenu />

export default AudioAnalyzer
